import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  quickstartSteps,
  SNIPPET_LANGS,
  WEBHOOK_VERIFY,
  type SnippetLang,
} from '@prakash-software/shared/developers';
import { Tabs } from '../../components/Tabs';
import { serverOrigin } from '../../lib/server-origin';
import { useAgentStore } from '../../stores/agent';
import styles from './admin.module.css';

// Admin → Developers. Onboarding for whoever integrates with Openline: where
// the reference and spec live (served by the server, routes/developers.ts),
// then the first requests and webhook verification, with the same samples the
// reference shows (@prakash-software/shared/developers).

const muted = { color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)' } as const;
const plainLink = { textDecoration: 'none' } as const;

/** `code` spans in the shared step text. */
function richText(text: string): ReactNode[] {
  return text.split(/(`[^`]+`)/).map((part, i) =>
    part.startsWith('`') && part.endsWith('`') ? <code key={i} className={styles.mono}>{part.slice(1, -1)}</code> : part,
  );
}

function CodeBlock({ code, label }: { code: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <pre className={styles.embedBlock} style={{ wordBreak: 'normal', overflowX: 'auto', whiteSpace: 'pre', paddingRight: 72, margin: 0 }} aria-label={label}>
        {code}
      </pre>
      <button
        type="button"
        className={styles.buttonGhost}
        style={{ position: 'absolute', top: 6, right: 6, padding: '2px 10px' }}
        onClick={() => void navigator.clipboard?.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <li style={{ display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr)', gap: 'var(--inic-space-3)', marginTop: 'var(--inic-space-4)' }}>
      <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--inic-accent)', color: '#ffffff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 'var(--inic-size-xs)' }}>
        {n}
      </span>
      <div style={{ display: 'grid', gap: 'var(--inic-space-2)', minWidth: 0 }}>
        <h3 style={{ margin: 0, fontSize: 'var(--inic-size-base)' }}>{title}</h3>
        {children}
      </div>
    </li>
  );
}

export function AdminDevelopers() {
  const role = useAgentStore((s) => s.current?.role);
  const canManageKeys = role === 'super-admin' || role === 'account-admin';
  const [lang, setLang] = useState<SnippetLang>('curl');
  const origin = serverOrigin();
  const steps = quickstartSteps(origin);
  const verifyLang = lang === 'curl' ? 'node' : lang;
  const langTabs = SNIPPET_LANGS.map((l) => ({ id: l.id, label: l.label }));

  return (
    <section className={styles.section}>
      <p className={styles.kicker}>Developers</p>
      <h2 className={styles.title}>Build on Openline.</h2>
      <p style={{ ...muted, margin: 0 }}>
        The REST API lets your own systems read conversations and reply to visitors. Webhooks tell them when
        something happens. Both are documented in the API reference, which anyone with the link can read.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--inic-space-2)', marginTop: 'var(--inic-space-3)' }}>
        <a className={styles.button} style={plainLink} href={`${origin}/developers`} target="_blank" rel="noreferrer">API reference ↗</a>
        <a className={styles.buttonGhost} style={plainLink} href={`${origin}/api/v1/openapi.json`} target="_blank" rel="noreferrer">OpenAPI spec ↗</a>
        {canManageKeys && <Link className={styles.buttonGhost} style={plainLink} to="/admin/api-keys">API keys →</Link>}
      </div>
      <p style={{ ...muted, marginTop: 'var(--inic-space-2)' }}>
        Base URL <code className={styles.mono}>{origin}/api/v1</code>. Import the spec into Postman, Insomnia or a
        code generator by its URL.
      </p>

      <h3 className={styles.title} style={{ fontSize: 'var(--inic-size-lg)', marginTop: 'var(--inic-space-5)' }}>Getting started</h3>
      <Tabs items={langTabs} active={lang} onChange={(id) => setLang(id as SnippetLang)} ariaLabel="Code language" />
      <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {steps.map((s, i) => (
          <Step key={s.id} n={i + 1} title={s.title}>
            <p style={{ ...muted, margin: 0 }}>
              {richText(s.body)}
              {s.id === 'check-key' && !canManageKeys && ' Ask an account admin for a key.'}
            </p>
            <CodeBlock code={s.code[lang]} label={`${s.title} (${lang})`} />
          </Step>
        ))}
        <Step n={steps.length + 1} title="Receive webhooks">
          <p style={{ ...muted, margin: 0 }}>
            Add a hook in <strong>Admin → Sites → (a site) → Webhooks</strong> for the events you need. Each new hook
            gets a signing secret (<code className={styles.mono}>whsec_…</code>); check every request against it, over
            the raw body, before trusting it. Deduplicate on <code className={styles.mono}>X-Openline-Delivery</code>,
            since delivery is at least once.
          </p>
          {lang === 'curl' && <p style={{ ...muted, margin: 0 }}>No curl version of this one; here it is in Node.js.</p>}
          <CodeBlock code={WEBHOOK_VERIFY[verifyLang]} label={`Verify a webhook (${verifyLang})`} />
        </Step>
      </ol>
    </section>
  );
}
